import { useEffect, useState } from "react";
import { FileText } from "lucide-react";

function kindOf(mime = "") {
  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf") return "pdf";
  if (mime.startsWith("text/") || mime === "application/json") return "text";
  return "other";
}

/**
 * Pure rendering of a file's inline preview (image / pdf / text, with a
 * fallback for everything else) given an already-fetched `blob`. Each caller
 * fetches bytes its own way (owner: authenticated `ws()` blob; anonymous link:
 * public token blob; email invite: invitation-token blob) and passes the blob
 * in here, so the mime-branch JSX isn't duplicated three times.
 */
export default function InlinePreview({
  mimeType,
  name,
  blob,
  allowCopy = true,
}) {
  const kind = kindOf(mimeType);
  const [objectUrl, setObjectUrl] = useState(null);
  const [textBody, setTextBody] = useState("");

  useEffect(() => {
    if (!blob || kind === "other") return undefined;
    let revoked = false;
    let url;
    if (kind === "text") {
      blob
        .text()
        .then((text) => !revoked && setTextBody(text.slice(0, 50_000)));
    } else {
      url = URL.createObjectURL(blob);
      setObjectUrl(url);
    }
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
      setObjectUrl(null);
    };
  }, [blob, kind]);

  const copyBlockProps = allowCopy
    ? {}
    : {
        onCopy: (e) => e.preventDefault(),
        onCut: (e) => e.preventDefault(),
        onContextMenu: (e) => e.preventDefault(),
      };

  if (kind === "image" && objectUrl) {
    return (
      <div
        {...copyBlockProps}
        className={`flex h-full items-center justify-center p-4 ${allowCopy ? "" : "select-none"}`}
      >
        <img
          src={objectUrl}
          alt={name}
          draggable={allowCopy}
          className="max-h-full max-w-full rounded-lg"
        />
      </div>
    );
  }
  if (kind === "pdf" && objectUrl) {
    return (
      <div
        {...copyBlockProps}
        className={`h-full w-full ${allowCopy ? "" : "select-none"}`}
      >
        <iframe
          title={name}
          src={`${objectUrl}#toolbar=0&navpanes=0`}
          sandbox={allowCopy ? undefined : ""}
          className="h-full w-full"
        />
      </div>
    );
  }
  if (kind === "text") {
    return (
      <pre
        {...copyBlockProps}
        className={`whitespace-pre-wrap p-5 text-xs leading-relaxed text-slate-700 ${
          allowCopy ? "" : "select-none"
        }`}
      >
        {textBody}
      </pre>
    );
  }
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-400">
      <FileText className="h-12 w-12" />
      <p className="text-sm">No preview available for this file type.</p>
      <p className="text-xs">{mimeType || "unknown type"}</p>
    </div>
  );
}
